/**
 * ContractModal — VEN-08: Crear / Editar Contrato
 */
import { todayISO } from '../../../lib/utils'
import { useState } from 'react'
import DatePicker from '@/components/DatePicker'
import { Loader2 } from 'lucide-react'
import {
  useContract,
  useCustomers,
  useCreateContract,
  useUpdateContract,
  type Contract,
} from '../hooks/useSales'
import { useSellers } from '@/modules/users/hooks/useUsers'
import { useGlobalCustomFields } from '@/modules/config/hooks/useConfig'
import CustomFieldsForm, { mergeCustomFields, type Personalizado } from '@/components/CustomFieldsForm'
import { Modal } from '@/components/ds/Modal'

interface ContractModalProps {
  contract?: Contract | null
  onClose: () => void
}

const CURRENCIES = ['CLP', 'USD', 'UF', 'EUR', 'JPY']

const inputCls =
  'w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary'

/**
 * 🔴 Al editar, el formulario se arma con el contrato COMPLETO y no con la fila
 * del listado: `ContractListSerializer` no trae notas, OC, condición de pago ni
 * personalizados, y como el payload los manda siempre, guardar los pisaba con
 * vacío (#1487). Por eso se pide el detalle y el form se monta recién cuando
 * llega — `useState` solo toma el valor inicial del primer render.
 */
export function ContractModal({ contract, onClose }: ContractModalProps) {
  const { data: full, isError } = useContract(contract?.id ?? '')
  if (!contract) return <ContractForm contract={null} onClose={onClose} />
  if (!full) {
    return (
      <Modal open onClose={onClose} title={`Editar Contrato ${contract.contract_number}`} size="2xl">
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-content-muted">
          {isError
            ? 'No se pudo cargar el contrato. Cerrar y volver a intentar.'
            : <><Loader2 size={16} className="animate-spin" />Cargando contrato…</>}
        </div>
      </Modal>
    )
  }
  return <ContractForm key={full.id} contract={full} onClose={onClose} />
}

function ContractForm({ contract, onClose }: ContractModalProps) {
  const isEdit = !!contract

  const [customerId, setCustomerId] = useState(contract?.customer ?? '')
  const [sellerId, setSellerId] = useState((contract as any)?.sales_rep ?? '')
  const [title, setTitle] = useState(contract?.title ?? '')
  const [startDate, setStartDate] = useState(
    contract?.start_date ?? todayISO()
  )
  const [endDate, setEndDate] = useState(contract?.end_date ?? '')
  const [contractValue, setContractValue] = useState(contract?.contract_value ?? '')
  const [currency, setCurrency] = useState(contract?.currency ?? 'CLP')
  const [purchaseOrder, setPurchaseOrder] = useState(contract?.purchase_order ?? '')
  const [paymentMethod, setPaymentMethod] = useState<'contado' | 'credito'>(
    contract?.payment_method ?? 'contado'
  )
  const [paymentTermDays, setPaymentTermDays] = useState(String(contract?.payment_term_days ?? 0))
  const [notes, setNotes] = useState(contract?.notes ?? '')
  // 🔴 Formato estándar `[{tipo, nombre, valor}]`, el mismo de ventas y compras
  // (#1752). Antes el modal guardaba `{nombre: valor}` y, si el contrato traía la
  // lista (los importados de marva), los mostraba vacíos y al guardar los borraba.
  // Un contrato con el formato viejo se convierte al abrirlo.
  const [personalizados, setPersonalizados] = useState<Personalizado[]>(() => {
    const guardados = contract?.personalizados
    if (Array.isArray(guardados)) return guardados as Personalizado[]
    if (guardados && typeof guardados === 'object') {
      return Object.entries(guardados as Record<string, string>)
        .map(([nombre, valor]) => ({ tipo: 'string', nombre, valor: String(valor ?? '') }))
    }
    return []
  })
  const [apiError, setApiError] = useState<string | null>(null)

  const { data: customersData } = useCustomers({ active: 'true', page_size: 9999 })
  const { data: sellersData } = useSellers()
  const sellers = sellersData?.sellers ?? []
  const { data: customFieldsData } = useGlobalCustomFields(true, 'contratos')
  const customFields = customFieldsData ?? []

  const create = useCreateContract()
  const update = useUpdateContract(contract?.id ?? '')
  const isPending = create.isPending || update.isPending


  const handleSave = () => {
    if (!customerId || !startDate) return
    setApiError(null)
    const payload = {
      customer: customerId,
      sales_rep: sellerId || null,
      title,
      start_date: startDate,
      end_date: endDate || null,
      contract_value: contractValue || null,
      currency,
      purchase_order: purchaseOrder,
      payment_method: paymentMethod,
      payment_term_days: parseInt(paymentTermDays || '0', 10),
      notes,
      personalizados: mergeCustomFields(customFields, personalizados),
    }
    const onError = (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      setApiError(err.response?.data?.error?.message ?? 'Error al guardar el contrato')
    }
    if (isEdit) {
      update.mutate(payload as Parameters<typeof update.mutate>[0], { onSuccess: onClose, onError })
    } else {
      create.mutate(payload as Parameters<typeof create.mutate>[0], { onSuccess: onClose, onError })
    }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? `Editar Contrato ${contract?.contract_number}` : 'Nuevo Contrato'} size="2xl"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={isPending || !customerId || !startDate}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isPending && <Loader2 size={14} className="animate-spin" />}
          {isEdit ? 'Guardar Cambios' : 'Crear Contrato'}
        </button>
      </>}
    >
      {/* Body */}
      <div className="overflow-y-auto flex-1 space-y-4">
        {/* Cliente y vendedor */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contractmodal-cliente" className="block text-xs font-medium text-content-muted mb-1">Cliente *</label>
            <select id="contractmodal-cliente"
              value={customerId}
              onChange={e => setCustomerId(e.target.value)}
              disabled={isEdit}
              className={`${inputCls} disabled:bg-surface-muted disabled:text-content-muted`}
            >
              <option value="">Seleccionar cliente...</option>
              {customersData?.customers.map(c => (
                <option key={c.id} value={c.id}>{c.legal_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="contractmodal-vendedor" className="block text-xs font-medium text-content-muted mb-1">Vendedor</label>
            <select id="contractmodal-vendedor" value={sellerId ?? ''} onChange={e => setSellerId(e.target.value)} className={inputCls}>
              <option value="">Sin vendedor</option>
              {sellers.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name || s.full_name}{!s.has_system_access ? ' (sin acceso)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Título */}
        <div>
          <label htmlFor="contractmodal-titulo-campana" className="block text-xs font-medium text-content-muted mb-1">Título / Campaña</label>
          <input id="contractmodal-titulo-campana"
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Ej. Campaña Invierno"
            className={inputCls}
          />
        </div>

        {/* Vigencia */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contractmodal-inicio-de-vigencia" className="block text-xs font-medium text-content-muted mb-1">Inicio de vigencia *</label>
            <DatePicker id="contractmodal-inicio-de-vigencia" value={startDate} onChange={setStartDate} />
          </div>
          <div>
            <label htmlFor="contractmodal-fin-de-vigencia" className="block text-xs font-medium text-content-muted mb-1">Fin de vigencia</label>
            <DatePicker id="contractmodal-fin-de-vigencia" value={endDate} onChange={setEndDate} />
            <p className="text-xs text-content-subtle mt-1">Vacío = indefinido.</p>
          </div>
        </div>

        {/* Valor + moneda + OC */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="col-span-1 sm:col-span-1">
            <label htmlFor="contractmodal-valor-pactado" className="block text-xs font-medium text-content-muted mb-1">Valor pactado</label>
            <input id="contractmodal-valor-pactado"
              type="number"
              value={contractValue ?? ''}
              onChange={e => setContractValue(e.target.value)}
              min="0"
              className={`${inputCls} text-right`}
            />
          </div>
          <div>
            <label htmlFor="contractmodal-moneda" className="block text-xs font-medium text-content-muted mb-1">Moneda</label>
            <select id="contractmodal-moneda" value={currency} onChange={e => setCurrency(e.target.value)} className={inputCls}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-span-2 sm:col-span-2">
            <label htmlFor="contractmodal-oc-del-cliente" className="block text-xs font-medium text-content-muted mb-1">OC del cliente</label>
            <input id="contractmodal-oc-del-cliente"
              type="text"
              value={purchaseOrder}
              onChange={e => setPurchaseOrder(e.target.value)}
              placeholder="Ej. OC-9001"
              className={inputCls}
            />
          </div>
        </div>

        {/* Condiciones de pago */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="contractmodal-forma-de-pago" className="block text-xs font-medium text-content-muted mb-1">Forma de pago</label>
            <select id="contractmodal-forma-de-pago"
              value={paymentMethod}
              onChange={e => setPaymentMethod(e.target.value as 'contado' | 'credito')}
              className={inputCls}
            >
              <option value="contado">Contado</option>
              <option value="credito">Crédito</option>
            </select>
          </div>
          <div>
            <label htmlFor="contractmodal-plazo-dias" className="block text-xs font-medium text-content-muted mb-1">Plazo (días)</label>
            <input id="contractmodal-plazo-dias"
              type="number"
              value={paymentTermDays}
              onChange={e => setPaymentTermDays(e.target.value)}
              min="0"
              disabled={paymentMethod === 'contado'}
              className={`${inputCls} disabled:bg-surface-muted disabled:text-content-subtle`}
            />
          </div>
        </div>

        {/* Notas */}
        <div>
          <label htmlFor="contractmodal-notas" className="block text-xs font-medium text-content-muted mb-1">Notas</label>
          <textarea id="contractmodal-notas"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Observaciones, glosa de la campaña, horarios..."
            className={`${inputCls} resize-none`}
          />
        </div>

        {/* Campos personalizados (scope contratos) */}
        {customFields.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-content-muted mb-2">Campos Adicionales</label>
            <CustomFieldsForm
              fields={customFields}
              value={mergeCustomFields(customFields, personalizados)}
              onChange={setPersonalizados}
            />
          </div>
        )}

        {apiError && (
          <div className="bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">
            {apiError}
          </div>
        )}
      </div>

      {/* Footer */}
    </Modal>
  )
}
